/**
 * preloadRecovery — recover a stale tab when a lazy module chunk 404s.
 *
 * Module SPAs (moduleRegistry.ts) are `React.lazy(() => import('./x/public'))`,
 * so each is a separate content-hashed chunk. When the server redeploys, a
 * long-open tab still references the OLD hashed filenames; the next lazy
 * `import()` fetches a URL that no longer exists → 404 → the module tile's
 * ErrorBoundary shows a dead "Module failed to load". Vite emits a
 * `vite:preloadError` event for exactly this case.
 *
 * We recover by reloading ONCE to pull the fresh entry bundle (which knows the
 * new chunk names). A short debounce (persisted in sessionStorage) prevents an
 * infinite reload loop when a chunk is *genuinely* gone (a real 404, not a
 * stale-deploy mismatch): after one reload attempt inside the window we stop
 * and let the tile show its error. A later redeploy in the same session still
 * recovers, because the stamp is older than the window by then.
 */

/** sessionStorage key holding the epoch-ms of our last recovery reload. */
export const RELOAD_STAMP_KEY = 'vibe_preload_reload_at';
/** Don't reload twice inside this window — a second failure means a real 404. */
export const RELOAD_DEBOUNCE_MS = 10_000;

/**
 * Decide whether to reload for a preload error, and stamp the attempt.
 * Pure/injectable so the debounce logic is unit-testable without touching the
 * real `window`/`sessionStorage`. Returns true the first time (and after the
 * debounce window elapses), false inside the window.
 */
export function shouldReloadOnPreloadError(now: number, storage: Storage): boolean {
  const raw = storage.getItem(RELOAD_STAMP_KEY);
  const last = raw === null ? NaN : Number(raw);
  if (Number.isFinite(last) && now - last < RELOAD_DEBOUNCE_MS) {
    return false;
  }
  storage.setItem(RELOAD_STAMP_KEY, String(now));
  return true;
}

/**
 * Wire the `vite:preloadError` listener. Call once at app entry (main.tsx).
 * Safe no-op in non-browser/SSR contexts.
 */
export function installPreloadErrorRecovery(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event) => {
    if (!shouldReloadOnPreloadError(Date.now(), window.sessionStorage)) return;
    // Suppress Vite's default (rethrow) — we're handling it by reloading.
    event.preventDefault();
    window.location.reload();
  });
}
